import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	DocumentWorkflowTemplateVisibleIn,
	GetKYCInvestorInstanceRes,
	KYCWorkflowFileItemRes,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { getMIPKycFormsTemplates } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getInvestorFormsForReview } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMipFormsWithAttachment } from '@dealstack/frontend/shared/hooks/useMipFormsWithAttachment';

interface Props {
	mipId?: string;
	currentInvestor?: GetKYCInvestorInstanceRes;
}

export const useFormStepPillFiles = ({
	mipId,
	currentInvestor,
}: Props): KYCWorkflowFileItemRes[] => {
	const { dealId } = useParams();
	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;

	const { data } = useQuery({
		queryKey: [API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_GET, mipId],
		queryFn: () => getMIPKycFormsTemplates({ archived: false }),
		enabled: !!mipId,
	});

	const { data: attachedForms } = useMipFormsWithAttachment(
		{ dealId: dealId ?? '', programmeId: mipId ?? '', kycWorkflowId },
		!!kycWorkflowId
	);

	const { data: submissionsData } = useQuery({
		queryKey: [
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
			dealId,
			mipId,
			kycWorkflowId,
		],
		queryFn: () =>
			getInvestorFormsForReview({
				dealId: dealId ?? '',
				programmeId: mipId ?? '',
				kycWorkflowId: kycWorkflowId ?? '',
			}),
		enabled: !!mipId && !!dealId && !!kycWorkflowId,
	});

	const attachedTemplateIds = useMemo(
		() =>
			new Set(
				(attachedForms ?? [])
					.filter((form) => form.isAttached)
					.map((form) => form.templateId)
			),
		[attachedForms]
	);

	return useMemo(() => {
		if (!currentInvestor) {
			return [];
		}

		const audienceVisibleIn = currentInvestor.piv
			? DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
			: DocumentWorkflowTemplateVisibleIn.KYC_INDIVIDUAL;

		const requiredTemplates =
			data?.payload?.filter(
				(template) =>
					template.visibleIn.includes(audienceVisibleIn) &&
					attachedTemplateIds.has(template.id)
			) ?? [];

		const submissionByTemplateId = new Map(
			(submissionsData?.payload ?? []).map(
				(submission) => [submission.templateId, submission] as const
			)
		);

		return requiredTemplates.map((template) => {
			const submission = submissionByTemplateId.get(template.id);
			return {
				id: submission?.instanceId ?? template.id,
				fileName: submission?.fileName ?? template.name,
				status: submission?.status ?? null,
				isInherited: submission?.subjectAction === SubjectAction.NOT_YET_ACTED,
				isAwaitingCertifier: false,
			};
		});
	}, [data?.payload, currentInvestor, attachedTemplateIds, submissionsData?.payload]);
};
